
import * as React from 'react';
import { X, Crown, Sparkles, ArrowRight, Bell, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

type TimeLeft = {
  hours: number;
  minutes: number;
  seconds: number;
};

/**
 * Fallback version of SignupPromotion that doesn't use framer-motion
 * Use this if there are build issues with framer-motion
 */
const SignupPromotionFallback: React.FC = () => {
  const [showPopup, setShowPopup] = React.useState(false);
  const [showFloating, setShowFloating] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    // Don't show promotion if user is signed in or if already dismissed
    if (user || localStorage.getItem('promotionDismissed')) {
      setShowPopup(false);
      setShowFloating(user ? false : true);
      return;
    }

    // Show popup on mount with a slight delay for better UX
    const popupTimer = setTimeout(() => {
      setShowPopup(true);
    }, 1500);

    // Add scroll listener to show/hide floating banner
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowFloating(true);
        setShowPopup(false);
      } else if (window.scrollY < 100) {
        setShowFloating(false);
        if (!localStorage.getItem('promotionDismissed')) {
          setShowPopup(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      clearTimeout(popupTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [user]);

  // If user is logged in, don't show any promotion
  if (user) {
    return null;
  }

  const handleDismiss = () => {
    setShowPopup(false);
    localStorage.setItem('promotionDismissed', 'true');
  };

  const handleFloatingDismiss = () => {
    setShowFloating(false);
    localStorage.setItem('promotionDismissed', 'true');
  };

  const handleSignUp = () => {
    // Store in localStorage that user came from promo
    localStorage.setItem('fromBonusPromo', 'true');
    navigate('/auth');
  };

  return (
    <>
      {/* Full-screen popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-black border border-[#9E7C47]/30 rounded-xl p-6 max-w-md w-full relative overflow-hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              className="absolute top-3 right-3 z-50 text-white/50 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors"
              aria-label="Close popup"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Decorative elements */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#9E7C47]/30 rounded-full blur-xl" />
            <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-[#9E7C47]/20 rounded-full blur-xl" />
            
            <div className="flex flex-col items-center text-center relative z-10">
              <Bell className="absolute top-0 right-0 h-6 w-6 text-[#9E7C47] animate-bounce" />
              
              <div className="w-20 h-20 rounded-full bg-[#9E7C47]/20 flex items-center justify-center mb-4">
                <Crown className="h-10 w-10 text-[#9E7C47]" />
              </div>
              
              <div className="inline-flex items-center px-3 py-1 mb-3 rounded-full border border-[#9E7C47]/30 bg-[#9E7C47]/10">
                <Sparkles className="h-4 w-4 text-[#9E7C47] mr-2" />
                <span className="text-xs font-medium">Invitation Only</span>
              </div>
              
              <h2 className="text-3xl font-bold mb-2 text-gradient">Join the Elite Circle</h2>
              <p className="text-white/80 mb-4">
                Exclusive beta access for select investors. Be among the first to experience the future of wealth management.
              </p>
              
              {/* Limited spots indicator */}
              <div className="w-full bg-white/5 rounded-lg p-3 mb-6 border border-[#9E7C47]/20">
                <div className="flex items-center gap-2 text-white/90 mb-2">
                  <Users className="h-4 w-4 text-[#9E7C47]" />
                  <span className="text-sm font-semibold">Limited to 1000 founding members</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5 mb-1">
                  <div 
                    className="bg-[#9E7C47] h-2.5 rounded-full transition-all duration-1000 ease-out w-3/4" 
                  />
                </div>
                <p className="text-xs text-white/60 text-right">
                  <span className="text-[#9E7C47] font-mono">Limited spots remaining</span>
                </p>
              </div>
              
              <div className="w-full">
                <Button 
                  className="w-full bg-[#9E7C47] hover:bg-[#9E7C47]/90 group mb-4 font-semibold py-6 hover:scale-105 transition-transform"
                  onClick={handleSignUp}
                >
                  Request Access
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              
              <button
                onClick={handleDismiss}
                className="text-sm text-white/50 hover:text-white/70"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating bottom banner (mobile-friendly) */}
      {showFloating && !showPopup && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-3 flex justify-center transition-transform duration-300">
          <div className="bg-black border border-[#9E7C47]/30 rounded-xl p-3 sm:p-4 max-w-lg w-full flex flex-col sm:flex-row items-center justify-between shadow-lg shadow-black/30 backdrop-blur-md bg-opacity-95">
            <div className="flex items-center mb-2 sm:mb-0">
              <div className="w-10 h-10 rounded-full bg-[#9E7C47]/20 flex items-center justify-center mr-3 shrink-0 hover:scale-110 transition-transform">
                <Crown className="h-5 w-5 text-[#9E7C47]" />
              </div>
              <div className="text-center sm:text-left">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-medium text-white">Exclusive Access</p>
                  <span className="bg-[#9E7C47]/20 text-[#9E7C47] text-xs px-1 rounded">
                    Invite Only
                  </span>
                </div>
                <p className="text-xs text-white/70">Limited beta program - Request access today!</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div>
                <Button 
                  size="sm"
                  className="bg-[#9E7C47] hover:bg-[#9E7C47]/90 group flex-1 sm:flex-none font-medium hover:scale-105 transition-transform"
                  onClick={handleSignUp}
                >
                  Join Waitlist
                  <ArrowRight className="ml-1 h-3 w-3 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <button
                onClick={handleFloatingDismiss}
                className="p-1.5 text-white/50 hover:text-white rounded-full hover:bg-white/10"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupPromotionFallback;
